import React, { useState, useEffect, useMemo, Fragment } from 'react';
import ReactDOMServer from 'react-dom/server';
import { FormattedMessage } from 'react-intl';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { ArrowRight } from 'react-feather';
import PropTypes from 'prop-types';
import { sum, propOr } from 'ramda';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import momentDurationFormatSetup from 'moment-duration-format';
import { groupJourneysByLowestFare } from 'pages/LowestFareFinder/LowestFareFinder.utils';

import { Container } from 'admin-components/components';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { localeToLanguage } from 'utils/locale';
import { ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC, displaySeatsLeftBounds } from 'constants/booking';
import currencySymbols from 'constants/currencies';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { eurostarAndAddonLegFaresMatches } from 'components/shared/JourneyDigest/JourneyDigest.utils';
import { BP, SP, STD, WC_COMP, COMPANION, CLASSES_OF_SERVICE_BY_TYPE, EVAC, UNASSIGNED } from 'constants/classOfService';
import classesOfService from 'constants/propTypes/classesOfService';
import { getDisplayPriceForSummatedFares } from 'utils/summatedFaresExchangePrice';
import { AVAILABLE_SEATS_ORDERING, COMPANION_CLASSES } from 'constants/inventoryClass';
import { getInventoryClassByCode } from 'utils/inventoryClass';
import { classOfServiceShape, passengersShape } from 'constants/propTypes';
import { brandCode } from 'constants/brandCodes';
import { LUGGAGE_STORAGE_CUTOFF_MINUTES } from 'constants/luggage';
import { buildTrainDisruptions, getLegDisruption } from 'utils/trainDisruptions';
import TrainOccupancyModal from 'components/shared/TrainOccupancy';
import Seat from 'components/ui/SeatMapLegend/Seat';

import JourneyDetailsSection from './JourneyDetailsSection';
import TrainDisruptionTooltip from './TrainDisruption/TrainDisruptionTooltip';
import TrainDisruptionWarning from './TrainDisruption/TrainDisruptionWarning';
import ClickableCell from './Cell/ClickableCell';
import Cell from './Cell/Cell';
import {
  buildColumns,
  getClassFare,
  groupByTimeOfDay,
  getLoyaltyPoints,
  getNumberOfSeatsLeft,
  getPromoDiscount,
  isBetweenRange,
  isAvailableTrain,
  isClassOfAccomodationBookable,
  journeyIsIndirect,
  getEurostarLegs,
  addNumberOfStationsToJourney,
  sortBasedOnNumberOfStations,
  isLuggageSoldOut,
  showTwoClassesOfServicesForCarrierBeingExchanged,
  getUpgradePriceDifferences
} from './SearchResultsTable.utils';
import {
  CenteredText,
  SearchResultsContainer,
  StyledSearchDiv,
  StyledTable,
  StyledLinkButton,
  StyledArrowContainer,
  StyledTrainNumber,
  StyledAvailabilities,
  StyledDepartureTime,
  StyledTrainOccupancyButton
} from './SearchResultsTables.styles';

momentDurationFormatSetup(moment);

const flowForwardPrice = {
  GBP: 40,
  EUR: 50,
  USD: 56
};

const generateAvailableSeats = (fares, currency, formatMessage, formatNumber, isEvacSearch = false, shouldOverridePrice = false) => {
  let storedFares = [];
  return (
    [...fares]
      ?.sort((a, b) => {
        const aClass = a?.buckets?.[0]?.code?.[0];
        const bClass = b?.buckets?.[0]?.code?.[0];

        if (!aClass || !bClass) {
          return 0;
        }

        return AVAILABLE_SEATS_ORDERING.indexOf(aClass) - AVAILABLE_SEATS_ORDERING.indexOf(bClass);
      })
      ?.map(({ buckets, classOfService, canBeBooked }) => {
        if (!buckets?.length) {
          return null;
        }

        if (classOfService?.code === EVAC && !isEvacSearch) {
          return null;
        }

        return buckets.map(({ availability, physicalAvailability, price, code: bucketCode }) => {
          const inventoryClass = getInventoryClassByCode(bucketCode);

          const currentClassOfService = COMPANION_CLASSES.has(inventoryClass) ? COMPANION : classOfService?.name;
          const isAOPPhysicalOnly =
            shouldOverridePrice && canBeBooked && !!availability && !!physicalAvailability && availability === physicalAvailability;
          const hasRequiredAvailabilityData = isAOPPhysicalOnly || !!availability;
          const hasValidPrice = isAOPPhysicalOnly || price >= 0;

          if (
            !classOfService ||
            !physicalAvailability ||
            !hasRequiredAvailabilityData ||
            !hasValidPrice ||
            storedFares.find((sf) => sf === currentClassOfService)
          ) {
            return null;
          }

          storedFares.push(currentClassOfService);
          const formattedPrice = `${price < 0 ? '-' : ''}${currencySymbols[currency]}${formatNumber(Math.abs(price), {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          })}
        `;
          const messageId = isAOPPhysicalOnly
            ? 'searchResults.seatAvailabilityAOP'
            : currentClassOfService !== COMPANION
            ? 'searchResults.seatAvailability'
            : `searchResults.${currentClassOfService}SeatAvailability`;
          const textParams = isAOPPhysicalOnly
            ? {
                classOfService: currentClassOfService,
                physicalAvailability
              }
            : {
                classOfService: currentClassOfService,
                physicalAvailability,
                availability: Math.min(availability, physicalAvailability),
                price: formattedPrice
              };

          return (
            <StyledAvailabilities key={bucketCode} data-cy="availability-seats">
              {formatMessage({ id: messageId }, textParams)}
            </StyledAvailabilities>
          );
        });
      }) || null
  );
};

const generateAssistanceAnimals = ({ service, animals, formatMessage, dogOnlySearch }) => {
  const animalsData = animals?.find(({ trainNumber }) => trainNumber === service);
  if (!animalsData) return null;

  return (
    <>
      {!dogOnlySearch && <div>---</div>}
      {formatMessage(
        { id: `assistanceAnimals.availabilitiesMessage` },
        {
          available: Math.max(4 - animalsData?.total, 0),
          booked: animalsData?.total
        }
      )}
      {animalsData?.carriages.length > 0 && (
        <>
          {' '}
          {formatMessage(
            { id: `assistanceAnimals.availabilitiesMessageCoaches` },
            {
              coaches: animalsData?.carriages.map(({ carriage }) => carriage)
            }
          )}
        </>
      )}
    </>
  );
};

const generateAssistance = ({ service, assistance, formatMessage, isValidUser }) => {
  const assistanceData = assistance?.find(({ trainNumber }) => trainNumber === service);
  if (!assistanceData || !isValidUser) return null;

  return (
    <StyledAvailabilities>
      {formatMessage(
        { id: `assistance.availabilitiesMessage` },
        {
          booked: assistanceData?.total ?? 0
        }
      )}
    </StyledAvailabilities>
  );
};

const generateStorage = ({ storage, formatMessage, isValidUser, departed }) => {
  if (!isValidUser || departed) return null;
  return (
    <StyledAvailabilities>
      {formatMessage(
        { id: `storage.availabilitiesMessage` },
        {
          available: storage ? storage : 0
        }
      )}
    </StyledAvailabilities>
  );
};

const getBaseCells = ({
  journey,
  isABSOrADS,
  isJourneyDetailsSectionVisible,
  toggleJourneyDetailsSection,
  currency = 'GBP',
  formatMessage,
  formatNumber,
  animalsPerTrain,
  animalsPerTrainLoading,
  assistancePerTrain,
  assistancePerTrainLoading,
  dogOnlySearch,
  isEsUser,
  isExchange,
  isWheelchairSearch,
  isEvacSearch,
  shouldOverridePrice = false,
  disruptions,
  disruptionTooltipId,
  showTrainOccupancy
}) => {
  const {
    timing: { departs, arrives, duration },
    fares
  } = journey;

  const numberOfChanges = fares[0].legs.length - 1;
  const isIndirect = journeyIsIndirect(isABSOrADS, journey);
  const hasNotAvailableSeats = !isAvailableTrain(fares, dogOnlySearch, animalsPerTrain, assistancePerTrain);
  const isValidUser = isEsUser || isExchange;
  const getFares = isWheelchairSearch
    ? fares.filter(({ classOfService }) => CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(classOfService.code))
    : fares;
  const eurostarLegs = getEurostarLegs(getFares[0]?.legs);
  const isERTrain = eurostarLegs.every(({ serviceType }) => serviceType.code === serviceTypeCodes.GREENSPEED);
  return {
    isHighlighted: (getFares[0]?.legs ?? []).some((leg) => getLegDisruption({ disruptions, leg })),
    departAt:
      animalsPerTrainLoading || assistancePerTrainLoading ? (
        <FormattedMessage id="generic.loading" />
      ) : (
        <Cell innerBorderColor="transparent" id={`depart_${departs}`}>
          <StyledDepartureTime>
            {eurostarLegs.length > 0 && (
              <StyledTrainOccupancyButton
                type="button"
                data-cy="train-occupancy-btn"
                $isERTrain={isERTrain}
                onClick={() => showTrainOccupancy(eurostarLegs)}
              >
                <Seat />
              </StyledTrainOccupancyButton>
            )}
            <span> {departs} </span>
          </StyledDepartureTime>
          <div data-cy="departed-train">
            {getFares[0].legs.map((leg, legIndex) => {
              const hasAvailable =
                [serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED].includes(leg.serviceType.code) && !hasNotAvailableSeats;
              const tooltipCutoff = moment(`${leg?.timing?.departureDate}T${leg?.timing?.departs}`)
                .subtract(LUGGAGE_STORAGE_CUTOFF_MINUTES, 'minutes')
                .format('YYYY-MM-DDTHH:mm');
              const hasDeparted = moment(tooltipCutoff).isBefore(moment().format('YYYY-MM-DDTHH:mm'));

              let storageAvailability = 0;

              for (const fare of fares) {
                const currentLeg = fare.legs[legIndex];
                const storage = currentLeg?.addOnProducts?.find(
                  ({ availabilityOfClassOfService }) => availabilityOfClassOfService
                )?.availabilityOfClassOfService;

                if (storage) {
                  storageAvailability = storage;
                  break;
                }
              }

              return (
                <Fragment key={`${leg?.serviceType?.code}-${leg?.serviceName}`}>
                  {legIndex > 0 ? ' | ' : ''}
                  <TrainDisruptionWarning disruption={getLegDisruption({ disruptions, leg })} tooltipId={disruptionTooltipId} />
                  <StyledTrainNumber
                    data-html
                    disabled={!hasAvailable || isIndirect}
                    data-for={`depart_${departs}`}
                    data-tip-disable={!hasAvailable || isIndirect}
                    data-tip={ReactDOMServer.renderToString(
                      <>
                        {!dogOnlySearch &&
                          generateAvailableSeats(fares, currency, formatMessage, formatNumber, isEvacSearch, shouldOverridePrice)}
                        {generateAssistanceAnimals({ service: leg?.serviceName, animals: animalsPerTrain, formatMessage, dogOnlySearch })}
                        {generateAssistance({ service: leg?.serviceName, assistance: assistancePerTrain, formatMessage, isValidUser })}
                        {generateStorage({
                          storage: storageAvailability,
                          formatMessage,
                          isValidUser,
                          departed: hasDeparted
                        })}
                      </>
                    )}
                  >
                    {leg.serviceType.code} {leg.serviceName}
                  </StyledTrainNumber>
                </Fragment>
              );
            })}
            <ReactTooltip id={`depart_${departs}`} />
          </div>
        </Cell>
      ),
    duration: (
      <Cell innerBorderColor="transparent" id="duration">
        <CenteredText> {moment.duration(duration, 'minutes').format('h[hrs] mm[mins]')} </CenteredText> <ArrowRight space="false" />
        <span size="small">
          {' '}
          {isIndirect ? (
            <StyledLinkButton data-cy="expand-journey-details-btn" onClick={toggleJourneyDetailsSection}>
              {numberOfChanges > 0 && <FormattedMessage id="searchResults.indirect" values={{ numberOfChanges }} />}
              {numberOfChanges === 0 && <FormattedMessage id="searchResults.moreInfo" />}{' '}
              <StyledArrowContainer isOpened={isJourneyDetailsSectionVisible}>{'>'}</StyledArrowContainer>
            </StyledLinkButton>
          ) : (
            <FormattedMessage id="searchResults.direct" />
          )}
        </span>{' '}
      </Cell>
    ),
    arriveAt: (
      <Cell innerBorderColor="transparent" id={`arrive_${arrives}`}>
        <span> {arrives} </span>{' '}
      </Cell>
    )
  };
};

const getClassOfServiceCellProps =
  ({
    isGroupBooking,
    journey,
    selectFare,
    selected,
    currency,
    showFareDifference = false,
    originalTotalPrice,
    requestedProductFamilies,
    isExchange,
    isFlowForward,
    lowestFares = {},
    isLoyaltyBooking = false,
    isMember = false,
    isABSOrADS = false,
    isExchangingMultipleLegsSummated = false,
    isExchangingSingleLegOfASummated = false,
    isExchangingADirect = false,
    isExchangingAThroughFare = false,
    isSummatedFareSelectionEnabled = false,
    legsBeingExchanged,
    isOverridingRules = false,
    shouldOverridePrice = false,
    classOfServiceToExchange,
    animalsPerTrain,
    exchangedClassesOfService,
    assistancePerTrain,
    isEsUser,
    extras
  }) =>
  (classOfService) => {
    const { fares, isTimeRestricted, isExistingTrain, isSameTrain, timing } = journey;
    const fareData = getClassFare(classOfService, fares, exchangedClassesOfService, isExchangingSingleLegOfASummated);

    if (!fareData)
      return {
        fareData,
        cellProps: {
          id: `${timing.departs}-${timing.arrives}-${classOfService}-price`,
          hasAvailableFares: false,
          canBeBooked: true,
          currency
        }
      };

    const seatsLeft = isGroupBooking ? 0 : getNumberOfSeatsLeft(fareData);
    const getBookingItems = propOr([], 'bookingItems');
    const bookingItems = getBookingItems(fareData);
    const totalPrice = sum(bookingItems.map((item) => item.price));
    const isSameTrains = isSameTrain && isSameTrain(fareData);
    const isExchangingSingleSNLegOfASummatedFare =
      isSummatedFareSelectionEnabled &&
      fareData.legs.length === 1 &&
      fareData.legs?.[0]?.serviceType.code === serviceTypeCodes.SNCF &&
      isExchangingSingleLegOfASummated;
    const isExchangingSNCBLeg = fareData.legs?.some((leg) => leg.serviceType?.code === serviceTypeCodes.SNCB);
    const serviceTypes = fareData.legs?.map(({ serviceType }) => serviceType?.code);
    const isSNSummatedFare =
      fareData.isSummatedFare &&
      fareData.legs.length > 1 &&
      serviceTypes.includes(serviceTypeCodes.SNCF) &&
      (serviceTypes.includes(serviceTypeCodes.EUROSTAR) || serviceTypes.includes(serviceTypeCodes.GREENSPEED));
    const isSNCBSummatedFare =
      fareData.isSummatedFare &&
      fareData.legs.length > 1 &&
      serviceTypes.includes(serviceTypeCodes.SNCB) &&
      (serviceTypes.includes(serviceTypeCodes.EUROSTAR) || serviceTypes.includes(serviceTypeCodes.GREENSPEED));
    const fromSNSummatedToSNSummated = isSummatedFareSelectionEnabled && isExchangingMultipleLegsSummated && isSNSummatedFare;
    const isExchangingSingleSummatedFareLeg =
      isSummatedFareSelectionEnabled && fareData.legs.length === 1 && isExchangingSingleLegOfASummated;
    const classOfAccomodationCanBeBooked = isClassOfAccomodationBookable({
      fareData,
      isExchange,
      isMember,
      isExchangingSingleSummatedFareLeg,
      classOfService,
      classOfServiceToExchange,
      legsBeingExchanged
    });
    const hasLuggageSoldOut = isLuggageSoldOut({ extras, fareData, legsBeingExchanged });

    let displayPrice = getDisplayPriceForSummatedFares({
      isGroupBooking,
      totalPrice,
      fareData,
      showFareDifference,
      originalTotalPrice,
      isExchange,
      isFlowForward,
      isExchangingMultipleLegsSummated,
      isExchangingSingleLegOfASummated,
      isExchangingADirect,
      isExchangingAThroughFare,
      isSummatedFareSelectionEnabled,
      legsBeingExchanged,
      isSameTrains,
      isOverridingRules,
      shouldOverridePrice: classOfService === classOfServiceToExchange && shouldOverridePrice,
      isExchangingSingleSNLegOfASummatedFare,
      isSNCBSummatedFare,
      isSNSummatedFare,
      oneExchangedItemHasAOPActive: shouldOverridePrice,
      isExchangingSameClassOfService: classOfService === classOfServiceToExchange,
      hasLuggageSoldOut
    });

    const lowestFareOfTheDay = Math.min(...Object.values(lowestFares).filter(Boolean));
    const loyaltyPoints = fareData.productFamily.familyGroup === productFamilyCodes.EUROSTAR_LOYALTY ? getLoyaltyPoints(fareData) : null;

    const fareProductFamilyCode = fareData.productFamily.familyGroup;

    const isDifferentProductFamily = requestedProductFamilies && !requestedProductFamilies.includes(fareProductFamilyCode);

    const faresAvailableForJourneyWithAddOns = eurostarAndAddonLegFaresMatches(bookingItems, isABSOrADS);

    const isFareSelected =
      fareData.classOfService.code === selected?.classOfService?.code &&
      fareData.legs.length === selected?.legs?.length &&
      fareData.legs.every(({ serviceIdentifier }, index) => serviceIdentifier === selected.legs?.[index]?.serviceIdentifier);

    const hasAnimalsSoldOut =
      animalsPerTrain?.length > 0 &&
      animalsPerTrain?.find(({ trainNumber }) => trainNumber === fareData?.legs?.[0]?.serviceName)?.total >= 4;

    const promoDiscount = getPromoDiscount(fareData);

    if (fareData.cappedTrain) {
      // set display price to hardcoded values based on currency & no of tickets
      displayPrice = flowForwardPrice[currency] * fareData.cappedTrain.exchangeItems.length;
    }

    return {
      fareData,
      cellProps: {
        id: `${timing.departs}-${timing.arrives}-${classOfService}-${fareData?.legs?.[0]?.trainNumber}-price`,
        hasAvailableFares: bookingItems.length > 0 && faresAvailableForJourneyWithAddOns,
        canBeBooked: classOfAccomodationCanBeBooked,
        cappedTrainFreeSeats: fareData.cappedTrain != null,
        displayPrice,
        loyaltyPoints,
        isTimeRestricted,
        isExistingTrain: isExistingTrain && isExistingTrain(fareData, classOfService),
        onClick: () => {
          selectFare(fareData, journey);
        },
        isSelected: selected && isFareSelected,
        innerBorderColor: 'black',
        innerBorderWidth: 3,
        currency,
        isDifferentProductFamily,
        fareProductFamilyCode,
        seatsLeft: isBetweenRange(seatsLeft, displaySeatsLeftBounds[0], displaySeatsLeftBounds[1]) ? seatsLeft : 0,
        isLowestFare: isLoyaltyBooking ? loyaltyPoints === lowestFareOfTheDay : displayPrice === lowestFareOfTheDay,
        promoDiscount,
        classOfService,
        hasAnimalsSoldOut,
        isExchangingSingleSNLegOfASummatedFare,
        isExchangingSNCBLeg,
        fromSNSummatedToSNSummated,
        hasLuggageSoldOut
      }
    };
  };

const buildRows =
  ({
    selectFare,
    selected,
    currency,
    isGroupBooking,
    originalTotalPrice,
    showFareDifference,
    isWheelchairSearch,
    isEvacSearch,
    isUnassignedSearch,
    requestedProductFamilies,
    isExchange,
    isFlowForward,
    columns,
    language,
    formatMessage,
    formatNumber,
    origin,
    destination,
    lowestFares = {},
    isLoyaltyBooking = false,
    isMember = false,
    isExchangingMultipleLegsSummated = false,
    isExchangingSingleLegOfASummated = false,
    isExchangingADirect = false,
    isExchangingAThroughFare = false,
    isSummatedFareSelectionEnabled = false,
    legsBeingExchanged,
    isOverridingRules = false,
    shouldOverridePrice = false,
    classOfServiceToExchange,
    animalsPerTrain,
    animalsPerTrainLoading,
    assistancePerTrain,
    assistancePerTrainLoading,
    dogOnlySearch,
    classesOfService,
    exchangedClassesOfService,
    isEsUser,
    extras,
    disruptions,
    disruptionTooltipId,
    passengers,
    showTrainOccupancy
  }) =>
  ({ journeys, journeyDetailsSectionsVisibility, toggleJourneyDetailsSection }) =>
    journeys.map((journey, index) => {
      const isABSOrADS =
        [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(origin) ||
        [ANY_BELGIAN_STATION_UIC, ADS_THROUGH_BRUSSELS_UIC].includes(destination);

      const classOfServiceCellProps = getClassOfServiceCellProps({
        isGroupBooking,
        showFareDifference,
        originalTotalPrice,
        journey,
        selectFare,
        selected,
        currency,
        requestedProductFamilies,
        isExchange,
        isFlowForward,
        lowestFares,
        isLoyaltyBooking,
        isMember,
        isABSOrADS,
        isExchangingMultipleLegsSummated,
        isExchangingSingleLegOfASummated,
        isExchangingADirect,
        isExchangingAThroughFare,
        isSummatedFareSelectionEnabled,
        legsBeingExchanged,
        isOverridingRules,
        shouldOverridePrice,
        classOfServiceToExchange,
        animalsPerTrain,
        assistancePerTrain,
        exchangedClassesOfService,
        extras
      });
      const classOfServiceCell = (code) => <ClickableCell {...classOfServiceCellProps(code).cellProps} />;
      const isIndirect = journeyIsIndirect(isABSOrADS, journey);
      const baseCells = getBaseCells({
        journey,
        isABSOrADS,
        toggleJourneyDetailsSection: () => toggleJourneyDetailsSection(index),
        isJourneyDetailsSectionVisible: journeyDetailsSectionsVisibility[index],
        currency,
        formatMessage,
        formatNumber,
        animalsPerTrain,
        animalsPerTrainLoading,
        assistancePerTrain,
        assistancePerTrainLoading,
        dogOnlySearch,
        isEsUser,
        isExchange,
        isWheelchairSearch,
        isEvacSearch,
        shouldOverridePrice,
        disruptions,
        disruptionTooltipId,
        showTrainOccupancy
      });

      const weelchairClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(code));

      if (isWheelchairSearch) {
        return {
          ...baseCells,
          ...weelchairClasses.reduce((cells, { code }) => ({ ...cells, [code]: classOfServiceCell(code) }), {}),
          additionalRows: [
            <JourneyDetailsSection
              key={`${journey?.timing?.departs}_${journey?.timing?.date}_${journey?.timing?.arrives}`}
              journey={journey}
              colSpan={columns.length}
              isIndirect={isIndirect}
              isVisible={journeyDetailsSectionsVisibility[index]}
              language={language}
              origin={origin}
              destination={destination}
            />
          ]
        };
      }

      const evacClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[EVAC].includes(code));

      if (isEvacSearch) {
        return {
          ...baseCells,
          ...evacClasses.reduce((cells, { code }) => ({ ...cells, [code]: classOfServiceCell(code) }), {}),
          additionalRows: [
            <JourneyDetailsSection
              key={`${journey?.timing?.departs}_${journey?.timing?.date}_${journey?.timing?.arrives}`}
              journey={journey}
              colSpan={columns.length}
              isIndirect={isIndirect}
              isVisible={journeyDetailsSectionsVisibility[index]}
              language={language}
              origin={origin}
              destination={destination}
            />
          ]
        };
      }

      const unassignedClasses = classesOfService.filter(({ code }) => CLASSES_OF_SERVICE_BY_TYPE[UNASSIGNED].includes(code));

      if (isUnassignedSearch) {
        return {
          ...baseCells,
          ...unassignedClasses.reduce((cells, { code }) => ({ ...cells, [code]: classOfServiceCell(code) }), {}),
          additionalRows: [
            <JourneyDetailsSection
              key={`${journey?.timing?.departs}_${journey?.timing?.date}_${journey?.timing?.arrives}`}
              journey={journey}
              colSpan={columns.length}
              isIndirect={isIndirect}
              isVisible={journeyDetailsSectionsVisibility[index]}
              language={language}
              origin={origin}
              destination={destination}
            />
          ]
        };
      }

      const nonWheelchairClasses = classesOfService.filter(
        ({ code }) =>
          !CLASSES_OF_SERVICE_BY_TYPE[WC_COMP].includes(code) &&
          !CLASSES_OF_SERVICE_BY_TYPE[EVAC].includes(code) &&
          !CLASSES_OF_SERVICE_BY_TYPE[UNASSIGNED].includes(code)
      );
      const classOfServiceCells = nonWheelchairClasses.map(({ code }) => ({ classOfService: code, ...classOfServiceCellProps(code) }));
      const visibleClassOfServiceCells = classOfServiceCells.filter(({ classOfService }) =>
        columns.some(({ key }) => key === classOfService)
      );
      const upgradePriceDifferences = isFlowForward
        ? {}
        : getUpgradePriceDifferences({ cells: visibleClassOfServiceCells, currency, passengers });

      return {
        ...baseCells,
        ...classOfServiceCells.reduce((cells, { classOfService, cellProps }) => {
          const upgradePrice = upgradePriceDifferences[classOfService];

          return {
            ...cells,
            [classOfService]: (
              <ClickableCell
                {...cellProps}
                upgradePriceDifference={upgradePrice?.difference}
                upgradePriceBreakdown={upgradePrice?.breakdown}
              />
            )
          };
        }, {}),
        additionalRows: [
          <JourneyDetailsSection
            key={`${journey?.timing?.departs}_${journey?.timing?.date}_${journey?.timing?.arrives}`}
            journey={journey}
            colSpan={columns.length}
            isIndirect={isIndirect}
            isVisible={journeyDetailsSectionsVisibility[index]}
            language={language}
            origin={origin}
            destination={destination}
          />
        ]
      };
    });

const formatHeader = (column) => (
  <>
    {column?.name ? column.name : <FormattedMessage id={`searchResults.${column.key}`} />}
    {column.currency && column.lowestFare >= 0 && (
      <div>
        <FormattedMessage id="journeySummaryBox.from" />:{' '}
        {column.isLoyaltyBooking ? (
          <FormattedMessage id="searchResults.pts" values={{ points: column.lowestFare }} />
        ) : (
          <FormattedCurrency currency={column.currency} amount={column.lowestFare ?? 0} />
        )}
      </div>
    )}
  </>
);

const SearchResultsTable = ({
  journeys,
  selectFare,
  selected,
  id,
  currency,
  isGroupBooking,
  isWheelchairSearch,
  isEvacSearch,
  isUnassignedSearch,
  showFareDifference,
  originalTotalPrice,
  requestedProductFamilies,
  isExchange,
  isFlowForward,
  origin,
  destination,
  intl: { locale, formatMessage, formatNumber },
  lowestFares = {},
  isLoyaltyBooking = false,
  isMember = false,
  isExchangingMultipleLegsSummated = false,
  isExchangingSingleLegOfASummated = false,
  isExchangingADirect = false,
  isExchangingAThroughFare = false,
  isSummatedFareSelectionEnabled = false,
  legsBeingExchanged = [],
  isOverridingRules = false,
  shouldOverridePrice = false,
  classOfServiceToExchange,
  animalsPerTrain,
  animalsPerTrainLoading,
  assistancePerTrain,
  assistancePerTrainLoading,
  dogOnlySearch,
  classesOfService,
  exchangedClassesOfService,
  serviceDetailsData = [],
  isEsUser,
  extras,
  trainDisruptions = [],
  startDate,
  lowestFareSearch,
  passengers = []
}) => {
  const journeysWithStations = serviceDetailsData.length > 0 ? addNumberOfStationsToJourney({ journeys, serviceDetailsData }) : journeys;
  const sortedJourneys = serviceDetailsData.length > 0 ? sortBasedOnNumberOfStations({ journeysWithStations }) : journeys;
  const { morningJourneys, afternoonJourneys } = lowestFareSearch
    ? groupJourneysByLowestFare(journeys, lowestFareSearch)
    : groupByTimeOfDay(sortedJourneys);
  const [morningJourneyDetailsSectionsVisibility, setMorningJourneyDetailsSectionsVisibility] = useState([]);
  const [afternoonJourneyDetailsSectionsVisibility, setAfternoonJourneyDetailsSectionsVisibility] = useState([]);
  const shouldDisplayTwoClassesOfService = showTwoClassesOfServicesForCarrierBeingExchanged({ legsBeingExchanged });

  const disruptions = useMemo(
    () => buildTrainDisruptions({ timetableServices: trainDisruptions, originUic: origin }),
    [trainDisruptions, origin]
  );
  const disruptionTooltipId = `train-disruption-${id}`;
  const [trainOccupancyLegs, setTrainOccupancyLegs] = useState(null);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const toggleMorningJourneyDetailsSection = (index) => {
    const updatedVisibility = [...morningJourneyDetailsSectionsVisibility];
    updatedVisibility[index] = !updatedVisibility[index];
    setMorningJourneyDetailsSectionsVisibility(updatedVisibility);
  };

  const toggleAfternoonJourneyDetailsSection = (index) => {
    const updatedVisibility = [...afternoonJourneyDetailsSectionsVisibility];
    updatedVisibility[index] = !updatedVisibility[index];
    setAfternoonJourneyDetailsSectionsVisibility(updatedVisibility);
  };

  const columns = buildColumns(
    currency,
    lowestFares,
    classesOfService,
    isWheelchairSearch,
    isEvacSearch,
    isUnassignedSearch,
    isLoyaltyBooking,
    shouldDisplayTwoClassesOfService,
    classOfServiceToExchange,
    legsBeingExchanged
  );
  const getRowsForJourneys = buildRows({
    selectFare,
    selected,
    currency,
    isGroupBooking,
    showFareDifference,
    originalTotalPrice,
    isWheelchairSearch,
    isEvacSearch,
    isUnassignedSearch,
    requestedProductFamilies,
    isExchange,
    isFlowForward,
    columns,
    language: localeToLanguage(locale).toLowerCase(),
    lowestFares,
    isLoyaltyBooking,
    formatMessage,
    formatNumber,
    isMember,
    isExchangingMultipleLegsSummated,
    isExchangingSingleLegOfASummated,
    isExchangingADirect,
    isExchangingAThroughFare,
    isSummatedFareSelectionEnabled,
    legsBeingExchanged,
    isOverridingRules,
    animalsPerTrain,
    animalsPerTrainLoading,
    assistancePerTrain,
    assistancePerTrainLoading,
    dogOnlySearch,
    origin,
    destination,
    shouldOverridePrice,
    classOfServiceToExchange,
    classesOfService,
    exchangedClassesOfService,
    isEsUser,
    extras,
    disruptions,
    disruptionTooltipId,
    passengers,
    showTrainOccupancy: setTrainOccupancyLegs
  });

  return (
    <StyledSearchDiv hasJourneys={journeys.length > 0} orientation="vertical" data-cy={id} width={6} spaceWithin space id={id}>
      <SearchResultsContainer id="search-results-table">
        <StyledTable
          formatHeader={formatHeader}
          columns={columns}
          isWheelchairSearch={isWheelchairSearch}
          isEvacSearch={isEvacSearch}
          isUnassignedSearch={isUnassignedSearch}
          headerHeight={70}
          borderWidth={2}
          innerBorder
          stripe
          rows={getRowsForJourneys({
            journeys: morningJourneys,
            journeyDetailsSectionsVisibility: morningJourneyDetailsSectionsVisibility,
            toggleJourneyDetailsSection: toggleMorningJourneyDetailsSection
          })}
          doAlternate={false}
        />
        {(lowestFareSearch ? afternoonJourneys.length > 0 : morningJourneys.length > 0 && afternoonJourneys.length > 0) && (
          <Container horizontalAlignment="center" palette="medium" spaceWithin id="afternoonBar">
            <span>
              {lowestFareSearch ? (
                <FormattedMessage
                  id="lowestFareFinder.moreExpensive"
                  values={{ class: formatMessage({ id: `lowestFareFinder.class.${lowestFareSearch.classOfService}` }) }}
                />
              ) : (
                <FormattedMessage id="searchResults.afternoonTrains" />
              )}
            </span>
          </Container>
        )}
        <StyledTable
          formatHeader={formatHeader}
          columns={columns}
          isWheelchairSearch={isWheelchairSearch}
          isEvacSearch={isEvacSearch}
          isUnassignedSearch={isUnassignedSearch}
          noHeader
          borderWidth={2}
          innerBorder
          stripe
          rows={getRowsForJourneys({
            journeys: afternoonJourneys,
            journeyDetailsSectionsVisibility: afternoonJourneyDetailsSectionsVisibility,
            toggleJourneyDetailsSection: toggleAfternoonJourneyDetailsSection
          })}
          doAlternate={false}
        />
        {disruptions.size > 0 && (
          <TrainDisruptionTooltip
            id={disruptionTooltipId}
            disruptions={disruptions}
            origin={origin}
            destination={destination}
            startDate={startDate}
          />
        )}
        {trainOccupancyLegs && (
          <TrainOccupancyModal
            isOpen
            legs={trainOccupancyLegs}
            onClose={() => setTrainOccupancyLegs(null)}
            shouldFetchAssistancesPerCoaches={isEsUser || isExchange}
          />
        )}
      </SearchResultsContainer>

      {journeys.length === 0 && (
        <Container horizontalAlignment="center" palette="small" spaceWithin id={`no-results-${id}`}>
          <span>
            <FormattedMessage id="searchResults.noresults" />
          </span>
        </Container>
      )}
    </StyledSearchDiv>
  );
};

SearchResultsTable.propTypes = {
  lowestFareSearch: PropTypes.shape({ price: PropTypes.number, classOfService: PropTypes.string }),
  id: PropTypes.string,
  origin: PropTypes.string,
  destination: PropTypes.string,
  journeys: PropTypes.arrayOf(
    PropTypes.shape({
      timing: PropTypes.shape({
        departs: PropTypes.string,
        arrives: PropTypes.string,
        duration: PropTypes.number
      }),
      fares: PropTypes.arrayOf(
        PropTypes.shape({
          class: PropTypes.shape({
            code: PropTypes.oneOf([STD, SP, BP, WC_COMP])
          }),
          classOfService: classOfServiceShape
        })
      )
    })
  ),
  selectFare: PropTypes.func.isRequired,
  selected: PropTypes.shape({
    timing: PropTypes.shape({
      departs: PropTypes.string,
      arrives: PropTypes.string,
      duration: PropTypes.number
    }),
    fares: PropTypes.arrayOf(
      PropTypes.shape({
        class: PropTypes.shape({
          code: PropTypes.oneOf([STD, SP, BP, WC_COMP])
        }),
        classOfService: classOfServiceShape
      })
    )
  }),
  requestedProductFamilies: PropTypes.arrayOf(PropTypes.string),
  currency: PropTypes.string,
  isGroupBooking: PropTypes.bool,
  isWheelchairSearch: PropTypes.bool,
  isEvacSearch: PropTypes.bool,
  isUnassignedSearch: PropTypes.bool,
  showFareDifference: PropTypes.bool,
  isExchange: PropTypes.bool,
  isFlowForward: PropTypes.bool,
  originalTotalPrice: PropTypes.number,
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
    formatMessage: PropTypes.func.isRequired,
    formatNumber: PropTypes.func.isRequired
  }).isRequired,
  lowestFares: PropTypes.object,
  isLoyaltyBooking: PropTypes.bool,
  isMember: PropTypes.bool,
  isExchangingMultipleLegsSummated: PropTypes.bool,
  isExchangingSingleLegOfASummated: PropTypes.bool,
  isExchangingADirect: PropTypes.bool,
  isExchangingAThroughFare: PropTypes.bool,
  isSummatedFareSelectionEnabled: PropTypes.bool,
  legsBeingExchanged: PropTypes.arrayOf(PropTypes.object),
  isOverridingRules: PropTypes.bool,
  shouldOverridePrice: PropTypes.bool,
  classOfServiceToExchange: PropTypes.string,
  animalsPerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  animalsPerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  assistancePerTrain: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  assistancePerTrainLoading: PropTypes.shape({
    outbound: PropTypes.bool,
    inbound: PropTypes.bool
  }),
  dogOnlySearch: PropTypes.bool,
  classesOfService: classesOfService,
  exchangedClassesOfService: PropTypes.shape({
    [brandCode.Lyria]: PropTypes.string,
    [brandCode.Eurostar]: PropTypes.string,
    [brandCode.SNCF]: PropTypes.string,
    [brandCode.INOUI]: PropTypes.string
  }),
  serviceDetailsData: PropTypes.shape({
    outbound: PropTypes.arrayOf(PropTypes.object),
    inbound: PropTypes.arrayOf(PropTypes.object)
  }),
  isEsUser: PropTypes.bool,
  extras: PropTypes.arrayOf(PropTypes.object),
  trainDisruptions: PropTypes.arrayOf(PropTypes.object),
  startDate: PropTypes.string,
  passengers: passengersShape
};

export default SearchResultsTable;
